import { type MetaFunction, json } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import Parser from "rss-parser";
import { z } from "zod";
import { CoverImage } from "~/components/blog-cover-image";

// Medium's feed still emits legacy `cdn-images-1.medium.com/max/<w>/<id>`
// URLs, which now answer 301 to `/v2/resize:fit:<w>/<id>`. Use the target form
// directly so the card thumbnail loads without depending on the redirect.
const normalizeMediumImage = (url: string) =>
	url.replace(
		/^https:\/\/cdn-images-1\.medium\.com\/max\/(\d+)\//,
		"https://cdn-images-1.medium.com/v2/resize:fit:$1/",
	);

const parseCoverImage = (content: string) => {
	const match = content.match(/<img[^>]+src="([^">]+)"/);
	return match ? normalizeMediumImage(match[1]) : "";
};

// Format on the server in a fixed time zone so the SSR markup and the client
// render produce the same text regardless of the visitor's time zone.
const formatPublishedAt = (publishedAt: string) => {
	const parts = new Intl.DateTimeFormat("en-GB", {
		timeZone: "Europe/Prague",
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	}).formatToParts(new Date(publishedAt));
	const part = (type: Intl.DateTimeFormatPartTypes) =>
		parts.find((p) => p.type === type)?.value ?? "";
	return `${part("day")}.${part("month")}.${part("year")}`;
};

const encodeTitle = (title: string): string => {
	const normalizedTitle = title
		.normalize("NFD")
		// biome-ignore lint/suspicious/noMisleadingCharacterClass: <explanation>
		.replace(/[\u0300-\u036f]/g, "");
	const lowerCaseTitle = normalizedTitle.toLowerCase();
	const cleanedTitle = lowerCaseTitle.replace(/[^\w\s-]/g, "");
	const encodedTitle = cleanedTitle.replace(/\s+/g, "-");
	return encodedTitle;
};

export const meta: MetaFunction = () => [{ title: "Blog | Aplikace Echo" }];

export const loader = async () => {
	const feed = await new Parser().parseURL(
		"https://medium.com/feed/@echowebblogs",
	);

	const posts = z
		.array(
			z.object({
				title: z.string().min(1),
				link: z.string().url(),
				coverImage: z.string().url(),
				publishedAt: z.string().min(1),
				guid: z.string(),
			}),
		)
		.parse(
			feed?.items?.map((item) => ({
				title: item.title,
				link: item.link,
				coverImage: parseCoverImage(item["content:encoded"]),
				publishedAt: item.pubDate,
				guid: item.guid?.split("p/")[1],
			})),
		);

	return json({
		posts: posts.map((post) => ({
			...post,
			publishedAt: formatPublishedAt(post.publishedAt),
		})),
	});
};

export default function Blog() {
	const { posts } = useLoaderData<typeof loader>();
	return (
		<div className="lg:px-32 font-thin mb-20 mt-10 h-full">
			<div className="container">
				<h2 className="font-bold text-xl mb-6">Blog</h2>
				<div className="grid grid-cols-1 gap-x-4 gap-y-8 lg:grid-cols-3 md:grid-cols-2">
					{posts.map((post) => {
						const slug = encodeTitle(post.title);
						return (
							<Link
								to={{ pathname: `/post/${slug}`, search: `?guid=${post.guid}` }}
								key={post.link}
							>
								<div className="pb-6 flex flex-col bg-white rounded-2xl gap-6 shadow-elevation-middle hover:shadow-elevation-high hover:opacity-90 transition-all">
									<div className="w-full h-[148px] overflow-hidden">
										<CoverImage src={post.coverImage} alt={post.title} />
									</div>
									<div className="flex px-6 gap-4 items-center">
										<div className="flex flex-col gap-2 flex-1">
											<h2 className="text-[22px] leading-7 font-extrabold">
												{post.title}
											</h2>
											<p className="text-sm">{post.publishedAt}</p>
										</div>
										{/* <ArrowIcon /> */}
									</div>
								</div>
							</Link>
						);
					})}
				</div>
			</div>
		</div>
	);
}
